import styled, { css } from 'styled-components'

import type { CSSClamp } from '../mixins'
import { cssClampMixin, hyphenateMixin } from '../mixins'

import type {
  ThemeFontSize,
  ThemeFontWeight,
  ThemeOnSurfaceColor,
  ThemeAccentColor,
  ThemeBaseColor,
} from 'theme/theme'
import { fontFamily, fontSize, fontWeight, color } from 'theme/utils'

type Props = {
  /**
   * Changes the type of rendered element.
   *
   * N.B. We are making this required in order to be explicit about the semantic level of the heading
   * for information architecure purposes.
   */
  as: 'h1' | 'h2'
  /**
   * Visually changes the headline size (opposed to using `as` which changes the semantic level)
   */
  $size?: Extract<ThemeFontSize, 'medium' | 'large'>
  /**
   * The font weight of the headline
   */
  $weight?: Extract<ThemeFontWeight, 'regular' | 'semibold' | 'bold'>
  /**
   * Changes the color of the headline
   */
  $color?: ThemeAccentColor | ThemeOnSurfaceColor | ThemeBaseColor
} & CSSClamp

/**
 * Headline component
 *
 * Use `as` to change the heading level, use `$size` to change the font size.
 *
 * Why not tie `as` with the font size? Because we want to be able to change
 * the font size without changing the heading level - e.g. sometimes you may need an h1, but
 * visually it needs to look like an h3.
 */
export const Headline = styled.h1<Props>`
  font-family: ${fontFamily('base')};
  font-size: ${fontSize('x3Large')};
  font-weight: ${fontWeight('semibold')};
  line-height: 1.2;
  color: ${color.onSurface('onSurfaceHigh')};

  ${({ $size = 'medium' }) => {
    switch ($size) {
      case 'medium':
        return css`
          font-size: ${fontSize('x3Large')};
        `
      case 'large':
        return css`
          font-size: ${fontSize('x4Large')};
          line-height: 1.17;
        `
    }
  }}

  ${({ $weight = 'semibold' }) => {
    switch ($weight) {
      case 'regular':
        return css`
          font-weight: ${fontWeight('regular')};
        `
      case 'semibold':
        return css`
          font-weight: ${fontWeight('semibold')};
        `
      case 'bold':
        return css`
          font-weight: ${fontWeight('bold')};
        `
    }
  }}

  ${({ $color = 'onSurfaceHigh' }) => css`
    color: ${color.all($color)};
  `}

  ${cssClampMixin}
  ${hyphenateMixin}
`
